import { inject, Injectable, OnDestroy, Signal, signal, WritableSignal } from '@angular/core';
import { FirmwareClient, SearchFirmwareRequest } from '../../../core/openapi/bff-admin-api';
import { BehaviorSubject, map, Observable, of, switchMap, tap } from 'rxjs';
import { FirmwaresMapper } from '../firmwares.mapper';
import { IFirmwareItem } from '../firmwares.model';
import { catchError } from 'rxjs/operators';
import { ToastService } from '@ui-lib/components';
import { TranslateService } from '@ngx-translate/core';
import { prepareRequest } from '../../../shared/utils/prepare-request.function';
import { generateExportFileName } from '../../../shared/utils/file-name.function';
import { downloadBlob } from '../../../shared/utils/download.function';
import { IBlobWrapper } from '../../../shared/types/blob-wrapper.interface';
import { LoadingState } from '../../../shared/types/loading.type';
import { outputError } from '../../../shared/utils/output-error.function';
import { DEFAULT_TABLE_PAGE_SIZE } from '../../../shared/const/pagination.constants';

@Injectable()
export class FirmwaresService implements OnDestroy {
  private readonly firmwareClient = inject(FirmwareClient);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);

  private readonly _firmwareLoadingState: WritableSignal<LoadingState> = signal<LoadingState>(
    LoadingState.LOADING,
  );
  private readonly _totalFirmwareCount: WritableSignal<number> = signal<number>(0);

  public readonly firmwareLoadingState: Signal<LoadingState> =
    this._firmwareLoadingState.asReadonly();
  public readonly totalFirmwareCount: Signal<number> = this._totalFirmwareCount.asReadonly();

  private readonly firmwareRequest$ = new BehaviorSubject<SearchFirmwareRequest>({
    skip: 0,
    take: DEFAULT_TABLE_PAGE_SIZE,
    includeTotalCount: true,
  });

  public readonly firmwares$: Observable<IFirmwareItem[]> = this.firmwareRequest$.pipe(
    tap(() => {
      this._firmwareLoadingState.set(LoadingState.LOADING);
    }),
    switchMap((request) =>
      this.firmwareClient.v1FirmwareSearchPost(request).pipe(
        catchError((err) => {
          this.toastService.error({
            message: this.translateService.instant(
              'monitoring.firmware.toast.firmware-list-fetch-error',
              { error: outputError(err, this.translateService.instant('errors.unknown')) },
            ),
          });

          this._totalFirmwareCount.set(0);
          return of({ items: [], totalCount: 0 });
        }),
      ),
    ),
    tap((res): void => this._totalFirmwareCount.set(res.totalCount ?? 0)),
    map(
      ({ items }): IFirmwareItem[] =>
        items?.map((item): IFirmwareItem => FirmwaresMapper.mapFromDto(item)) ?? [],
    ),
    tap(() => this._firmwareLoadingState.set(LoadingState.READY)),
  );

  public triggerFirmwareUpdate(params: Partial<SearchFirmwareRequest>): void {
    this.firmwareRequest$.next({
      ...this.firmwareRequest$.value,
      ...prepareRequest(params),
    });
  }

  public downloadFirmware(
    firmwareId: string,
    firmwareName: string,
    fileName: string,
  ): Observable<IBlobWrapper> {
    return this.firmwareClient
      .v1FirmwareIdFileGet(firmwareId, 'response', false, {
        httpHeaderAccept: 'application/zip, application/octet-stream, application/x-binary, */*',
        responseType: 'blob',
        // Принудительное приведение к any используется из-за несоответствия типа httpHeaderAccept
        // Нам нужно передать строку с MIME-типами, но метод ожидает другой тип
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any)
      .pipe(
        map(
          (response): IBlobWrapper => ({
            blob: response.body as Blob,
            filename: generateExportFileName(
              `firmware_${firmwareName}`,
              fileName.split('.').at(-1) ?? 'zip',
            ),
          }),
        ),
        tap(({ blob, filename }): void => {
          downloadBlob(blob, filename);
          this.toastService.success({
            message: 'monitoring.firmware.toast.download-successfully-started',
          });
        }),
        catchError((err) => {
          this.toastService.error({
            message: 'errors.file-downloading',
          });
          throw new Error(`Monitoring: Error occurred: ${err}`);
        }),
      );
  }

  public ngOnDestroy(): void {
    this.firmwareRequest$.complete();
  }
}
